import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Ban, Play, Snowflake, MapPin, Clock, Share2, ImagePlus, Pencil, Shield, Loader2, CheckCircle, XCircle, MessageSquare, Package, Plus } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { BRANCH_STATUSES, branchStatusLabel } from '@/hooks/use-geo';
import RegionDistrictSelect from '@/components/RegionDistrictSelect';
import LocationPicker from '@/components/LocationPicker';
import CatalogTab from '@/components/CatalogTab';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-error';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { intlLocale } from './adminLocale';

type MerchantResource = components['schemas']['MerchantResource'];
type BranchResource = components['schemas']['BranchResource'];
type UserResource = components['schemas']['UserResource'];
type DeviceResource = components['schemas']['DeviceResource'];
type MerchantStatusHistoryResource = components['schemas']['MerchantStatusHistoryResource'];

const statusLabels = (): Record<string, string> => ({
  new: i18n.t('adm_merchants.st_new'), active: i18n.t('adm_merchants.st_active'), blocked: i18n.t('adm_merchants.st_blocked'),
  frozen: i18n.t('adm_merchants.st_frozen'), pending: i18n.t('adm_merchants.st_pending'), rejected: i18n.t('adm_merchants.st_rejected'),
});
const statusColors: Record<string, string> = {
  new: 'bg-purple-100 text-purple-800', active: 'bg-green-100 text-green-800', blocked: 'bg-red-100 text-red-800',
  frozen: 'bg-blue-100 text-blue-800', pending: 'bg-yellow-100 text-yellow-800', rejected: 'bg-red-100 text-red-800',
};
const trustLevelLabels = (): Record<string, string> => ({ green: i18n.t('adm_merchantDetail.trust_green'), yellow: i18n.t('adm_merchantDetail.trust_yellow'), red: i18n.t('adm_merchantDetail.trust_red') });
const trustLevelColors: Record<string, string> = { green: 'bg-green-500', yellow: 'bg-yellow-500', red: 'bg-red-500' };
const roleLabels = (): Record<string, string> => ({ owner: i18n.t('adm_merchantDetail.role_owner'), manager: i18n.t('adm_merchantDetail.role_manager'), cashier: i18n.t('adm_merchantDetail.role_cashier'), viewer: i18n.t('adm_merchantDetail.role_viewer') });
const userStatusLabels = (): Record<string, string> => ({ pending: i18n.t('adm_merchants.st_pending'), active: i18n.t('adm_merchants.st_active'), blocked: i18n.t('adm_merchants.st_blocked') });
const emptyBranch = {
  name: '', address: '', phone: '', website: '', social_instagram: '', social_telegram: '', social_facebook: '',
  branch_code: '', region_id: null as number | null, district_id: null as number | null, status: 'active',
  is_visible_in_app: true,
};
const userStatusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800', active: 'bg-green-100 text-green-800', blocked: 'bg-red-100 text-red-800',
};

export default function AdminMerchantDetail() {
  const { id } = useParams<{ id: string }>();
  const merchantId = Number(id);
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: merchantData, isLoading: loadingMerchant } = useQuery({ queryKey: [`/admin/merchants/{merchant}`, { params: { path: { merchant: merchantId } } }] });
  const merchant = merchantData?.data as MerchantResource | undefined;

  const { data: branchesData } = useQuery({
    queryKey: ['/branches', { params: { query: { merchant_id: merchantId } as never } }],
  });
  const branches: BranchResource[] = branchesData?.data ?? [];

  const { data: usersData } = useQuery({
    queryKey: ['/staff', { params: { query: { merchant_id: merchantId } as never } }],
  });
  const users: UserResource[] = usersData?.data ?? [];

  const { data: devicesData } = useQuery({
    queryKey: ['/devices', { params: { query: { merchant_id: merchantId } as never } }],
  });
  const devices: DeviceResource[] = devicesData?.data ?? [];

  const { data: historyData } = useQuery({ queryKey: [`/admin/merchants/{merchant}/status-history`, { params: { path: { merchant: merchantId } } }] });
  const history: MerchantStatusHistoryResource[] = historyData?.data ?? [];

  const loading = loadingMerchant;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: [`/admin/merchants/{merchant}`] });
    queryClient.invalidateQueries({ queryKey: [`/admin/merchants/{merchant}/status-history`] });
    queryClient.invalidateQueries({ queryKey: ['/staff'] });
    queryClient.invalidateQueries({ queryKey: ['/branches'] });
  };

  // Status change dialog
  const [statusDialog, setStatusDialog] = useState<{ open: boolean; action: string }>({ open: false, action: '' });
  const [reason, setReason] = useState('');
  const [updating, setUpdating] = useState(false);

  // Edit merchant dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [editSaving, setEditSaving] = useState(false);

  // Staff role management
  const [roleDialog, setRoleDialog] = useState(false);
  const [roleUser, setRoleUser] = useState<UserResource | null>(null);
  const [newRole, setNewRole] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);
  const [userStatusSaving, setUserStatusSaving] = useState<number | null>(null);

  // Branch management (FR-96: platform admin runs a merchant's branches, as in Lovable)
  const [branchDialog, setBranchDialog] = useState(false);
  const [branchEditId, setBranchEditId] = useState<number | null>(null);
  const [branchForm, setBranchForm] = useState(emptyBranch);
  const [branchSaving, setBranchSaving] = useState(false);
  const [branchToggling, setBranchToggling] = useState<number | null>(null);

  // Approval fields
  const [trustLevel, setTrustLevel] = useState('');
  const [adminComment, setAdminComment] = useState('');

  if (loading) return <div className="p-6 text-muted-foreground">{t('adm_common.loading')}</div>;
  if (!merchant) return <div className="p-6 text-muted-foreground">{t('adm_merchantDetail.notFound')}</div>;

  if (trustLevel === '' && merchant.trust_level) {
    // Lazily seed the select once merchant data has arrived.
    setTrustLevel(merchant.trust_level);
  }

  const canChangeStatus = admin?.role === 'platform_owner' || admin?.role === 'support_admin';
  const currentStatus = merchant.platform_status || 'active';

  const updateMerchantFields = async (updates: Record<string, unknown>) => {
    const { error } = await apiClient.PATCH('/admin/merchants/{merchant}', {
      params: { path: { merchant: merchantId } },
      body: updates as never,
    });
    if (error) { toast.error(apiErrorMessage(error, t('adm_common.errorGeneric'))); return; }
    toast.success(t('adm_merchantDetail.saved'));
    invalidateAll();
  };

  const handleStatusChange = async () => {
    if (!merchant) return;
    const newStatus = statusDialog.action;
    if ((newStatus === 'blocked' || newStatus === 'frozen' || newStatus === 'rejected') && !reason.trim()) {
      toast.error(t('adm_merchantDetail.reasonRequired'));
      return;
    }
    setUpdating(true);
    try {
      if (newStatus === 'active' && (currentStatus === 'new' || currentStatus === 'rejected') && trustLevel) {
        await updateMerchantFields({ trust_level: trustLevel });
      }
      const { error } = await apiClient.PATCH('/admin/merchants/{merchant}/status', {
        params: { path: { merchant: merchantId } },
        body: { to_status: newStatus as never, reason: reason.trim() },
      });
      if (error) throw error;

      toast.success(t('adm_merchants.statusChanged', { status: statusLabels()[newStatus] }));
      setStatusDialog({ open: false, action: '' });
      setReason('');
      invalidateAll();
    } catch (e) {
      toast.error(apiErrorMessage(e, t('adm_merchantCreate.errorOccurred')));
    } finally {
      setUpdating(false);
    }
  };

  const openEditMerchant = () => {
    setEditForm({
      name: merchant.name || '', legal_name: merchant.legal_name || '', inn: merchant.inn || '',
      phone: merchant.phone || '', address: merchant.address || '',
      director_name: merchant.director_name || '', director_phone: merchant.director_phone || '',
      director_passport: merchant.director_passport || '',
      accountant_name: merchant.accountant_name || '', accountant_phone: merchant.accountant_phone || '',
      accountant_passport: merchant.accountant_passport || '',
      bank_name: merchant.bank_name || '', bank_code: merchant.bank_code || '', bank_account: merchant.bank_account || '',
      internal_account: merchant.internal_account || '',
    });
    setEditDialog(true);
  };

  const saveEditMerchant = async () => {
    setEditSaving(true);
    try {
      await updateMerchantFields(editForm);
      setEditDialog(false);
    } finally { setEditSaving(false); }
  };

  const openBranchCreate = () => {
    setBranchEditId(null);
    setBranchForm(emptyBranch);
    setBranchDialog(true);
  };

  const openBranchEdit = (b: BranchResource) => {
    setBranchEditId(b.id);
    setBranchForm({
      name: b.name || '', address: b.address || '', phone: b.phone || '', website: b.website || '',
      social_instagram: b.social_instagram || '', social_telegram: b.social_telegram || '', social_facebook: b.social_facebook || '',
      branch_code: b.branch_code || '', region_id: b.region_id ?? null, district_id: b.district_id ?? null,
      status: b.status || 'active', is_visible_in_app: b.is_visible_in_app ?? true,
    });
    setBranchDialog(true);
  };

  const saveBranch = async () => {
    if (!branchForm.name.trim()) { toast.error(t('adm_merchantDetail.branchNameRequired')); return; }
    setBranchSaving(true);
    try {
      const payload = { ...branchForm, name: branchForm.name.trim() };
      const { error } = branchEditId
        ? await apiClient.PATCH('/branches/{branch}', { params: { path: { branch: branchEditId } }, body: payload })
        : await apiClient.POST('/branches', { body: { ...payload, merchant_id: merchantId } });
      if (error) throw error;
      toast.success(branchEditId ? t('adm_merchantDetail.branchUpdated') : t('adm_merchantDetail.branchAdded'));
      setBranchDialog(false);
      invalidateAll();
    } catch (e) {
      toast.error(apiErrorMessage(e, t('adm_common.errorGeneric')));
    } finally { setBranchSaving(false); }
  };

  const toggleBranch = async (b: BranchResource) => {
    setBranchToggling(b.id);
    try {
      const { error } = await apiClient.PATCH('/branches/{branch}', {
        params: { path: { branch: b.id } },
        body: { is_active: !b.is_active },
      });
      if (error) throw error;
      invalidateAll();
    } catch (e) {
      toast.error(apiErrorMessage(e, t('adm_common.errorGeneric')));
    } finally { setBranchToggling(null); }
  };

  const openRoleChange = (u: UserResource) => {
    setRoleUser(u);
    setNewRole(u.roles[0] ?? '');
    setRoleDialog(true);
  };

  const saveRole = async () => {
    if (!roleUser || !newRole) return;
    setRoleSaving(true);
    try {
      const { error } = await apiClient.PATCH('/staff/{user}/role', {
        params: { path: { user: roleUser.id } },
        body: { role: newRole as never },
      });
      if (error) throw error;
      toast.success(t('adm_platformUsers.roleUpdated'));
      setRoleDialog(false);
      invalidateAll();
    } catch (e) {
      toast.error(apiErrorMessage(e, t('adm_common.errorGeneric')));
    } finally { setRoleSaving(false); }
  };

  const handleUserStatus = async (userId: number, action: 'approve' | 'unblock' | 'block') => {
    setUserStatusSaving(userId);
    try {
      const { error } = await apiClient.POST(`/staff/{user}/${action}` as '/staff/{user}/approve', {
        params: { path: { user: userId } },
      });
      if (error) throw error;
      toast.success(action === 'block' ? t('adm_merchantDetail.blockedToast') : t('adm_merchantDetail.activatedToast'));
      invalidateAll();
    } catch (e) {
      toast.error(apiErrorMessage(e, t('adm_common.errorGeneric')));
    } finally { setUserStatusSaving(null); }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/merchants')}>
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-foreground">{merchant.name}</h1>
          <p className="text-sm text-muted-foreground">{merchant.legal_name} · INN: {merchant.inn}</p>
        </div>
        <Badge variant="outline" className={statusColors[currentStatus] || ''}>
          {statusLabels()[currentStatus] || currentStatus}
        </Badge>
        {canChangeStatus && (
          <Button variant="outline" size="sm" onClick={openEditMerchant}>
            <Pencil size={14} className="mr-1" /> {t('adm_merchantDetail.edit')}
          </Button>
        )}
      </div>

      {/* Approval section for 'new' merchants */}
      {canChangeStatus && currentStatus === 'new' && (
        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle size={14} /> {t('adm_merchantDetail.reviewApplication')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">{t('adm_merchantDetail.trustLevel')}</Label>
                <Select value={trustLevel} onValueChange={setTrustLevel}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('adm_merchantDetail.selectLevel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="green">🟢 {t('adm_merchantDetail.trust_green')}</SelectItem>
                    <SelectItem value="yellow">🟡 {t('adm_merchantDetail.trust_yellow')}</SelectItem>
                    <SelectItem value="red">🔴 {t('adm_merchantDetail.trust_red')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('adm_merchantDetail.adminComment')}</Label>
              <Textarea
                value={adminComment}
                onChange={e => setAdminComment(e.target.value)}
                placeholder={t('adm_merchantDetail.commentPlaceholder')}
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={!trustLevel || updating}
                onClick={() => {
                  if (!trustLevel) { toast.error(t('adm_merchantDetail.selectTrustLevel')); return; }
                  setStatusDialog({ open: true, action: 'active' });
                }}
              >
                <CheckCircle size={14} className="mr-1" /> {t('adm_merchants.next_active')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive"
                disabled={updating}
                onClick={() => setStatusDialog({ open: true, action: 'rejected' })}
              >
                <XCircle size={14} className="mr-1" /> {t('adm_merchantDetail.reject')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!adminComment.trim() || updating}
                onClick={async () => {
                  setUpdating(true);
                  try {
                    await updateMerchantFields({ admin_comment: adminComment.trim() });
                    toast.success(t('adm_merchantDetail.commentSent'));
                  } finally { setUpdating(false); }
                }}
              >
                <MessageSquare size={14} className="mr-1" /> {t('adm_merchantDetail.sendComment')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trust level for non-new merchants */}
      {canChangeStatus && currentStatus !== 'new' && (
        <Card>
          <CardContent className="flex items-center gap-4 py-3">
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium">{t('adm_merchantDetail.trustLevelColon')}</span>
              {merchant.trust_level && (
                <>
                  <span className={`inline-block w-3 h-3 rounded-full ${trustLevelColors[merchant.trust_level] || ''}`} />
                  <span className="text-sm">{trustLevelLabels()[merchant.trust_level] || merchant.trust_level}</span>
                </>
              )}
              {!merchant.trust_level && <span className="text-sm text-muted-foreground">{t('adm_merchants.trust_none')}</span>}
            </div>
            <Select value={trustLevel} onValueChange={setTrustLevel}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder={t('adm_merchantDetail.selectLevel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="green">🟢 {t('adm_merchantDetail.trust_green')}</SelectItem>
                <SelectItem value="yellow">🟡 {t('adm_merchantDetail.trust_yellow')}</SelectItem>
                <SelectItem value="red">🔴 {t('adm_merchantDetail.trust_red')}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              disabled={!trustLevel || trustLevel === merchant.trust_level || updating}
              onClick={async () => {
                setUpdating(true);
                try {
                  await updateMerchantFields({ trust_level: trustLevel });
                } finally { setUpdating(false); }
              }}
            >
              {updating ? <Loader2 size={14} className="animate-spin" /> : t('adm_common.save')}
            </Button>
          </CardContent>
        </Card>
      )}
      {!canChangeStatus && currentStatus !== 'new' && merchant.trust_level && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('adm_merchantDetail.trustColon')}</span>
          <span className={`inline-block w-3 h-3 rounded-full ${trustLevelColors[merchant.trust_level] || ''}`} />
          <span className="text-sm">{trustLevelLabels()[merchant.trust_level] || merchant.trust_level}</span>
        </div>
      )}

      {/* Status actions */}
      {canChangeStatus && currentStatus !== 'new' && (
        <div className="flex gap-2">
          {currentStatus !== 'active' && (
            <Button size="sm" variant="outline" className="text-green-700" onClick={() => setStatusDialog({ open: true, action: 'active' })}>
              <Play size={14} className="mr-1" /> {t('adm_merchantDetail.activate')}
            </Button>
          )}
          {currentStatus !== 'blocked' && (
            <Button size="sm" variant="outline" className="text-destructive" onClick={() => setStatusDialog({ open: true, action: 'blocked' })}>
              <Ban size={14} className="mr-1" /> {t('adm_merchantDetail.block')}
            </Button>
          )}
          {currentStatus !== 'frozen' && (
            <Button size="sm" variant="outline" className="text-blue-700" onClick={() => setStatusDialog({ open: true, action: 'frozen' })}>
              <Snowflake size={14} className="mr-1" /> {t('adm_merchantDetail.freeze')}
            </Button>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="general">
        <TabsList className="flex-wrap">
          <TabsTrigger value="general">{t('adm_merchantDetail.tabGeneral')}</TabsTrigger>
          <TabsTrigger value="status">{t('adm_merchantDetail.tabStatusHistory')}</TabsTrigger>
          <TabsTrigger value="branches">{t('adm_merchantDetail.tabBranches', { count: branches.length })}</TabsTrigger>
          <TabsTrigger value="users">{t('adm_merchantDetail.tabStaff', { count: users.length })}</TabsTrigger>
          <TabsTrigger value="catalog">
            <Package size={14} className="mr-1" /> {t('adm_merchantDetail.tabCatalog')}
          </TabsTrigger>
          <TabsTrigger value="devices">{t('adm_merchantDetail.tabDevices', { count: devices.length })}</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Logo */}
            <Card className="md:col-span-2">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><ImagePlus size={14} /> Logo</CardTitle></CardHeader>
              <CardContent>
                {merchant.logo_url ? (
                  <img src={merchant.logo_url} alt="Logo" className="w-20 h-20 rounded-xl object-cover border border-border" />
                ) : (
                  <div className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center">
                    <ImagePlus size={24} className="text-muted-foreground" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Asosiy */}
            <Card>
              <CardHeader><CardTitle className="text-sm">{t('adm_merchantDetail.mainInfo')}</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label={t('adm_merchantDetail.name')} value={merchant.name} />
                <Row label={t('adm_merchants.legalName')} value={merchant.legal_name} />
                <Row label="INN" value={merchant.inn} />
                <Row label={t('adm_merchants.phone')} value={merchant.phone} />
                <Row label={t('adm_merchantCreate.address')} value={merchant.address} />
                <Row label={t('adm_merchants.created')} value={merchant.created_at ? new Date(merchant.created_at).toLocaleDateString(intlLocale()) : null} />
              </CardContent>
            </Card>

            {/* Lokatsiya */}
            <Card className="md:col-span-2">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><MapPin size={14} /> {t('adm_merchantCreate.location')}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {(merchant.latitude && merchant.longitude) ? (
                  <>
                    <LocationPicker
                      latitude={Number(merchant.latitude)}
                      longitude={Number(merchant.longitude)}
                      onChange={() => {}}
                      height="250px"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{merchant.latitude}, {merchant.longitude}</span>
                      <a
                        href={`https://www.google.com/maps?q=${merchant.latitude},${merchant.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        {t('adm_merchantDetail.openGoogleMaps')}
                      </a>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('adm_merchantDetail.noLocation')}</p>
                )}
              </CardContent>
            </Card>

            {/* Direktor */}
            <Card>
              <CardHeader><CardTitle className="text-sm">{t('adm_merchantCreate.director')}</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label={t('adm_merchantDetail.fullName')} value={merchant.director_name} />
                <Row label={t('adm_merchants.phone')} value={merchant.director_phone} />
                <Row label={t('adm_merchantDetail.passport')} value={merchant.director_passport} />
              </CardContent>
            </Card>

            {/* Buxgalter */}
            <Card>
              <CardHeader><CardTitle className="text-sm">{t('adm_merchantCreate.accountant')}</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label={t('adm_merchantDetail.fullName')} value={merchant.accountant_name} />
                <Row label={t('adm_merchants.phone')} value={merchant.accountant_phone} />
                <Row label={t('adm_merchantDetail.passport')} value={merchant.accountant_passport} />
              </CardContent>
            </Card>

            {/* Bank */}
            <Card>
              <CardHeader><CardTitle className="text-sm">{t('adm_merchantCreate.bankDetails')}</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label={t('adm_merchantCreate.bankName')} value={merchant.bank_name} />
                <Row label="MFO" value={merchant.bank_code} />
                <Row label={t('adm_merchantDetail.accountNo')} value={merchant.bank_account} />
                <Row label={t('adm_merchantDetail.internalAccount')} value={merchant.internal_account} />
              </CardContent>
            </Card>

            {/* Ish vaqti */}
            <Card>
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Clock size={14} /> {t('adm_merchantCreate.workingHours')}</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                {(() => {
                  const wh = merchant.working_hours as Record<string, string> | null;
                  if (!wh || Object.keys(wh).length === 0) return <span className="text-muted-foreground">—</span>;
                  const dayLabels: Record<string, string> = { mon: t('adm_merchantDetail.dayShort_mon'), tue: t('adm_merchantDetail.dayShort_tue'), wed: t('adm_merchantDetail.dayShort_wed'), thu: t('adm_merchantDetail.dayShort_thu'), fri: t('adm_merchantDetail.dayShort_fri'), sat: t('adm_merchantDetail.dayShort_sat'), sun: t('adm_merchantDetail.dayShort_sun') };
                  return ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(d => (
                    <Row key={d} label={dayLabels[d] || d} value={wh[d] || t('adm_merchantDetail.dayOff')} />
                  ));
                })()}
              </CardContent>
            </Card>

            {/* Ijtimoiy tarmoqlar */}
            <Card className="md:col-span-2">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Share2 size={14} /> {t('adm_merchantCreate.social')}</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Instagram" value={merchant.social_instagram} />
                <Row label="Telegram" value={merchant.social_telegram} />
                <Row label="Facebook" value={merchant.social_facebook} />
                <Row label={t('adm_merchantCreate.website')} value={merchant.website} />
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">{t('adm_merchantDetail.appVisibility')}</CardTitle>
                {merchant.is_verified ? (
                  <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" /> {t('adm_contracts.st_approved')}</Badge>
                ) : (
                  <Badge variant="outline">{t('adm_merchantDetail.notVerified')}</Badge>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <Row label={t('adm_merchantDetail.shortDescription')} value={merchant.description_short} />
                <Row label={t('adm_merchantDetail.fullDescription')} value={merchant.description_full} />
                <Row label={t('adm_merchantDetail.coverImage')} value={merchant.cover_image_url} />
                <Row label={t('adm_merchantDetail.cardImage')} value={merchant.card_image_url} />
                <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                  <span className="text-sm">{t('adm_merchantDetail.showInApp')}</span>
                  <Switch
                    checked={merchant.is_visible_in_app ?? true}
                    onCheckedChange={(v) => updateMerchantFields({ is_visible_in_app: v })}
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                  <span className="text-sm">{t('adm_merchantDetail.verifiedStore')}</span>
                  <Switch
                    checked={merchant.is_verified ?? false}
                    disabled={!canChangeStatus}
                    onCheckedChange={(v) => updateMerchantFields({ is_verified: v })}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="catalog">
          <CatalogTab merchantId={String(merchant.id)} />
        </TabsContent>

        <TabsContent value="status">
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adm_common.date')}</TableHead>
                  <TableHead>{t('adm_merchantDetail.previous')}</TableHead>
                  <TableHead>{t('adm_merchantDetail.new')}</TableHead>
                  <TableHead>{t('adm_merchantDetail.reason')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">{t('adm_merchantDetail.noHistory')}</TableCell></TableRow>
                ) : (
                  history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="text-xs">{new Date(h.changed_at).toLocaleString(intlLocale())}</TableCell>
                      <TableCell><Badge variant="outline" className={statusColors[h.from_status ?? ''] || ''}>{statusLabels()[h.from_status ?? ''] || h.from_status}</Badge></TableCell>
                      <TableCell><Badge variant="outline" className={statusColors[h.to_status] || ''}>{statusLabels()[h.to_status] || h.to_status}</Badge></TableCell>
                      <TableCell className="text-sm">{h.reason || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="branches">
          <div className="flex items-center justify-between mb-3 gap-3">
            <p className="text-sm text-muted-foreground">{t('adm_merchantDetail.branchesHint')}</p>
            {canChangeStatus && (
              <Button size="sm" onClick={openBranchCreate}><Plus size={14} className="mr-1" /> {t('adm_merchantDetail.addBranch')}</Button>
            )}
          </div>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adm_merchantDetail.name')}</TableHead>
                  <TableHead>{t('adm_merchantDetail.code')}</TableHead>
                  <TableHead>{t('adm_merchantCreate.address')}</TableHead>
                  <TableHead>{t('adm_merchants.phone')}</TableHead>
                  <TableHead>{t('adm_merchants.status')}</TableHead>
                  {canChangeStatus && <TableHead className="text-right">{t('adm_merchants.actions')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {branches.length === 0 ? (
                  <TableRow><TableCell colSpan={canChangeStatus ? 6 : 5} className="text-center text-muted-foreground py-8">{t('adm_merchantDetail.noBranches')}</TableCell></TableRow>
                ) : (
                  branches.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                      <TableCell className="font-mono text-xs">{b.branch_code || '—'}</TableCell>
                      <TableCell>{b.address}</TableCell>
                      <TableCell>{b.phone}</TableCell>
                      <TableCell className="space-x-1">
                        <Badge variant={b.is_active ? 'default' : 'secondary'}>{b.is_active ? t('adm_pricing.active') : t('adm_pricing.inactive')}</Badge>
                        <Badge variant="outline">{branchStatusLabel(b.status)}</Badge>
                        {b.is_visible_in_app === false && <Badge variant="outline">{t('adm_merchantDetail.hiddenInApp')}</Badge>}
                      </TableCell>
                      {canChangeStatus && (
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={branchToggling === b.id} onClick={() => toggleBranch(b)}>
                              {branchToggling === b.id ? <Loader2 size={14} className="animate-spin" /> : (b.is_active ? t('adm_merchantDetail.deactivate') : t('adm_merchantDetail.activate'))}
                            </Button>
                            <button onClick={() => openBranchEdit(b)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground" title={t('adm_merchantDetail.edit')}>
                              <Pencil size={14} />
                            </button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adm_platformUsers.name')}</TableHead>
                  <TableHead>{t('adm_merchants.phone')}</TableHead>
                  <TableHead>{t('adm_platformUsers.role')}</TableHead>
                  <TableHead>{t('adm_merchants.status')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">{t('adm_merchantDetail.noStaff')}</TableCell></TableRow>
                ) : (
                  users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.name || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{u.phone}</TableCell>
                      <TableCell>
                        {u.roles.map(role => (
                          <Badge key={role} variant="outline" className="capitalize mr-1">{roleLabels()[role] || role}</Badge>
                        ))}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={userStatusColors[u.status] || ''}>
                          {userStatusLabels()[u.status] || u.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {canChangeStatus && !u.roles.includes('owner') && (
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openRoleChange(u)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground" title={t('adm_platformUsers.changeRole')}>
                              <Shield size={14} />
                            </button>
                            {u.status !== 'active' && (
                              <button
                                onClick={() => handleUserStatus(u.id, u.status === 'pending' ? 'approve' : 'unblock')}
                                className="p-1.5 rounded-lg hover:bg-accent text-green-600"
                                title={t('adm_merchantDetail.activate')}
                                disabled={userStatusSaving === u.id}
                              >
                                {userStatusSaving === u.id ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                              </button>
                            )}
                            {u.status !== 'blocked' && (
                              <button
                                onClick={() => handleUserStatus(u.id, 'block')}
                                className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"
                                title={t('adm_merchantDetail.block')}
                                disabled={userStatusSaving === u.id}
                              >
                                {userStatusSaving === u.id ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                              </button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="devices">
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adm_merchantDetail.name')}</TableHead>
                  <TableHead>{t('adm_merchantDetail.serialNumber')}</TableHead>
                  <TableHead>{t('adm_merchantDetail.type')}</TableHead>
                  <TableHead>{t('adm_merchants.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">{t('adm_merchantDetail.noDevices')}</TableCell></TableRow>
                ) : (
                  devices.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell className="font-mono text-xs">{d.serial_number}</TableCell>
                      <TableCell className="capitalize">{d.device_type}</TableCell>
                      <TableCell><Badge variant={d.is_active ? 'default' : 'secondary'}>{d.is_active ? t('adm_pricing.active') : t('adm_pricing.inactive')}</Badge></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Status change dialog */}
      <AlertDialog open={statusDialog.open} onOpenChange={(open) => { if (!open) { setStatusDialog({ open: false, action: '' }); setReason(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('adm_merchantDetail.changeStatusTitle', { status: statusLabels()[statusDialog.action] })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('adm_merchantDetail.changeStatusConfirm', { name: merchant.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {(statusDialog.action === 'blocked' || statusDialog.action === 'frozen' || statusDialog.action === 'rejected') && (
            <div className="space-y-2">
              <Label>{t('adm_merchantDetail.reasonRequiredLabel')}</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder={t('adm_merchantDetail.reasonPlaceholder')} />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updating}>{t('adm_common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleStatusChange} disabled={updating}>
              {updating ? t('adm_common.saving') : t('adm_merchants.next_active')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Merchant Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t('adm_merchantDetail.editMerchantTitle')}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchantDetail.name')}</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchants.legalName')}</Label>
              <Input value={editForm.legal_name} onChange={e => setEditForm(f => ({ ...f, legal_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">INN</Label>
              <Input value={editForm.inn} onChange={e => setEditForm(f => ({ ...f, inn: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchants.phone')}</Label>
              <Input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">{t('adm_merchantCreate.address')}</Label>
              <Input value={editForm.address} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} />
            </div>
            <div className="sm:col-span-2 border-t pt-3 mt-1">
              <p className="text-xs font-medium text-muted-foreground mb-2">{t('adm_merchantCreate.director')}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchantCreate.fio')}</Label>
              <Input value={editForm.director_name} onChange={e => setEditForm(f => ({ ...f, director_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchants.phone')}</Label>
              <Input value={editForm.director_phone} onChange={e => setEditForm(f => ({ ...f, director_phone: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchantCreate.passport')}</Label>
              <Input value={editForm.director_passport} onChange={e => setEditForm(f => ({ ...f, director_passport: e.target.value }))} />
            </div>
            <div className="sm:col-span-2 border-t pt-3 mt-1">
              <p className="text-xs font-medium text-muted-foreground mb-2">{t('adm_merchantCreate.accountant')}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchantCreate.fio')}</Label>
              <Input value={editForm.accountant_name} onChange={e => setEditForm(f => ({ ...f, accountant_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchants.phone')}</Label>
              <Input value={editForm.accountant_phone} onChange={e => setEditForm(f => ({ ...f, accountant_phone: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchantCreate.passport')}</Label>
              <Input value={editForm.accountant_passport} onChange={e => setEditForm(f => ({ ...f, accountant_passport: e.target.value }))} />
            </div>
            <div className="sm:col-span-2 border-t pt-3 mt-1">
              <p className="text-xs font-medium text-muted-foreground mb-2">{t('adm_merchantCreate.bankDetails')}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchantCreate.bankName')}</Label>
              <Input value={editForm.bank_name} onChange={e => setEditForm(f => ({ ...f, bank_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">MFO</Label>
              <Input value={editForm.bank_code} onChange={e => setEditForm(f => ({ ...f, bank_code: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchantDetail.accountNo')}</Label>
              <Input value={editForm.bank_account} onChange={e => setEditForm(f => ({ ...f, bank_account: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchantDetail.internalAccount')}</Label>
              <Input inputMode="numeric" maxLength={20} value={editForm.internal_account} onChange={e => setEditForm(f => ({ ...f, internal_account: e.target.value.replace(/\D/g, '') }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>{t('adm_common.cancel')}</Button>
            <Button onClick={saveEditMerchant} disabled={editSaving}>
              {editSaving ? t('adm_common.saving') : t('adm_common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Branch Dialog */}
      <Dialog open={branchDialog} onOpenChange={setBranchDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{branchEditId ? t('adm_merchantDetail.editBranch') : t('adm_merchantDetail.newBranch')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('adm_merchantDetail.nameRequired')}</Label>
                <Input placeholder={t('adm_merchantDetail.branchNamePlaceholder')} value={branchForm.name} onChange={e => setBranchForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('adm_merchantDetail.branchCode')}</Label>
                <Input placeholder="F-001" value={branchForm.branch_code} onChange={e => setBranchForm(f => ({ ...f, branch_code: e.target.value }))} />
              </div>
            </div>
            <RegionDistrictSelect
              regionId={branchForm.region_id}
              districtId={branchForm.district_id}
              onChange={next => setBranchForm(f => ({ ...f, ...next }))}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('adm_merchantDetail.statusLabel')}</Label>
                <Select value={branchForm.status} onValueChange={v => setBranchForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BRANCH_STATUSES.map(st => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <div className="flex w-full items-center justify-between rounded-lg border px-3 py-2">
                  <span className="text-sm">{t('adm_merchantDetail.visibleInApp')}</span>
                  <Switch checked={branchForm.is_visible_in_app} onCheckedChange={v => setBranchForm(f => ({ ...f, is_visible_in_app: v }))} />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_merchantCreate.address')}</Label>
              <Input value={branchForm.address} onChange={e => setBranchForm(f => ({ ...f, address: e.target.value }))} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('adm_merchants.phone')}</Label>
                <Input placeholder="+998 90 123 45 67" value={branchForm.phone} onChange={e => setBranchForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('adm_merchantDetail.webSite')}</Label>
                <Input placeholder="https://" value={branchForm.website} onChange={e => setBranchForm(f => ({ ...f, website: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Instagram</Label>
                <Input value={branchForm.social_instagram} onChange={e => setBranchForm(f => ({ ...f, social_instagram: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Telegram</Label>
                <Input value={branchForm.social_telegram} onChange={e => setBranchForm(f => ({ ...f, social_telegram: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Facebook</Label>
                <Input value={branchForm.social_facebook} onChange={e => setBranchForm(f => ({ ...f, social_facebook: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBranchDialog(false)}>{t('adm_common.cancel')}</Button>
            <Button onClick={saveBranch} disabled={branchSaving}>
              {branchSaving ? t('adm_common.saving') : t('adm_common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('adm_platformUsers.changeRoleTitle')}: {roleUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>{t('adm_platformUsers.newRole')}</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">{t('adm_merchantDetail.roleOwnerOption')}</SelectItem>
                <SelectItem value="manager">{t('adm_merchantDetail.role_manager')}</SelectItem>
                <SelectItem value="cashier">{t('adm_merchantDetail.role_cashier')}</SelectItem>
                <SelectItem value="viewer">{t('adm_merchantDetail.role_viewer')}</SelectItem>
              </SelectContent>
            </Select>
            {newRole === 'owner' && (
              <p className="text-xs text-muted-foreground">{t('adm_merchantDetail.ownerTransferHint')}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(false)}>{t('adm_common.cancel')}</Button>
            <Button onClick={saveRole} disabled={roleSaving}>
              {roleSaving ? <><Loader2 size={14} className="animate-spin mr-1" /> {t('adm_common.saving')}</> : t('adm_common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value || '—'}</span>
    </div>
  );
}
